import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";
import { getDashboardRoute } from "../../utils/getDashboardRoute";
import { getAvailableRoles } from "../../utils/roleSwitcher";
import "../../assets/styles/logistics_handover.scss";

const MAX_HANDOVER_PHOTOS = 5;

function safeStr(v, fallback = "—") {
  if (v == null) return fallback;
  const s = String(v).trim();
  return s ? s : fallback;
}

function isoToYmd(v) {
  if (!v) return "";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

/** Same master resolution as `LogisticsQuoteDetailsPage` — RFQ payload stores country/state/city ids. */
function mapCountriesResponse(response) {
  const apiCountries = Array.isArray(response?.data)
    ? response.data
    : response?.data?.countries || response?.data?.master_countries || [];
  return apiCountries
    .map((country) => {
      const id = country.country_id ?? country.id ?? country.value;
      const label =
        country.country_name ?? country.country_label ?? country.label ?? country.name ?? country.country;
      return { id: id != null ? String(id) : "", label: label != null ? String(label) : "" };
    })
    .filter((c) => c.id && c.label);
}

function mapStatesResponse(response) {
  const apiStates = Array.isArray(response?.data) ? response.data : response?.data?.states || response?.data?.master_states || [];
  return apiStates
    .map((state) => {
      const id = state.state_id ?? state.id ?? state.value;
      const label = state.state_name ?? state.state_label ?? state.label ?? state.name ?? state.state;
      return { id: id != null ? String(id) : "", label: label != null ? String(label) : "" };
    })
    .filter((s) => s.id && s.label);
}

function mapCitiesResponse(response) {
  const apiCities = Array.isArray(response?.data) ? response.data : response?.data?.cities || response?.data?.master_cities || [];
  return apiCities
    .map((city) => {
      const id = city.city_id ?? city.id ?? city.value;
      const label = city.city_name ?? city.city_label ?? city.label ?? city.name ?? city.city;
      return { id: id != null ? String(id) : "", label: label != null ? String(label) : "" };
    })
    .filter((c) => c.id && c.label);
}

/** Client-side required checks aligned with `saveBuyerHandoverSchema` (notes optional). */
function validateHandoverForm(f) {
  const req = (key, label) => {
    const s = f?.[key] != null ? String(f[key]).trim() : "";
    if (!s) return `${label} is required.`;
    return null;
  };
  const fields = [
    ["pickupDate", "Pickup date"],
    ["pickupTime", "Pickup time"],
    ["handoverLocation", "Handover location"],
    ["contactPersonName", "Contact person name"],
    ["contactPhone", "Contact phone"],
    ["vehicleNumber", "Vehicle number"],
    ["driverName", "Driver name"],
    ["driverPhone", "Driver phone"],
    ["numberOfPackages", "Number of packages"],
    ["actualWeightKg", "Actual weight (kg)"],
    ["sealNumber", "Seal number"],
  ];
  for (const [key, label] of fields) {
    const err = req(key, label);
    if (err) return err;
  }
  const pkgs = Number(String(f.numberOfPackages).trim());
  if (!Number.isFinite(pkgs) || pkgs <= 0 || !Number.isInteger(pkgs)) {
    return "Number of packages must be a whole number greater than zero.";
  }
  const kg = Number(String(f.actualWeightKg).trim());
  if (!Number.isFinite(kg) || kg <= 0) {
    return "Actual weight (kg) must be a number greater than zero.";
  }
  return null;
}

async function resolveLocationLabels(countryId, stateId, cityId) {
  const cid = countryId != null && String(countryId).trim() !== "" ? String(countryId).trim() : "";
  const sid = stateId != null && String(stateId).trim() !== "" ? String(stateId).trim() : "";
  const cyid = cityId != null && String(cityId).trim() !== "" ? String(cityId).trim() : "";
  if (!cid) return { country: "—", state: "—", city: "—" };
  try {
    const cRes = await CommonService.get("/master/company-Country");
    const countries = mapCountriesResponse(cRes);
    const countryLabel = countries.find((c) => c.id === cid)?.label || cid;

    let stateLabel = "—";
    if (sid) {
      const sRes = await CommonService.get(`/master/company-State/${cid}`);
      const states = mapStatesResponse(sRes);
      stateLabel = states.find((s) => s.id === sid)?.label || sid;
    }

    let cityLabel = "—";
    if (cyid && sid) {
      const cityRes = await CommonService.get(`/master/company-City/${sid}`);
      const cities = mapCitiesResponse(cityRes);
      cityLabel = cities.find((c) => c.id === cyid)?.label || cyid;
    }

    return { country: countryLabel, state: stateLabel, city: cityLabel };
  } catch {
    return { country: cid, state: sid || "—", city: cyid || "—" };
  }
}

export default function LogisticsHandOverPage() {
  const { submissionId, quoteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dashboardHref = getDashboardRoute();

  const isProviderHandoverView = useMemo(() => {
    const p = location.pathname || "";
    return p.includes("/dashboard/logistics/rfqs/") && p.includes("/handover/");
  }, [location.pathname]);

  const [loading, setLoading] = useState(true);
  const [rfq, setRfq] = useState(null);
  const [quoteWrapper, setQuoteWrapper] = useState(null);
  const [resolvedLocations, setResolvedLocations] = useState(null);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [viewOnly, setViewOnly] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    pickupDate: "",
    pickupTime: "",
    handoverLocation: "",
    contactPersonName: "",
    contactPhone: "",
    vehicleNumber: "",
    driverName: "",
    driverPhone: "",
    numberOfPackages: "",
    actualWeightKg: "",
    sealNumber: "",
    notes: "",
  });

  useEffect(() => {
    if (!isProviderHandoverView) return undefined;
    if (!getAvailableRoles().some((r) => r.id === "logistics")) {
      toast.error("This handover view is only available for logistics providers.");
      navigate(getDashboardRoute(), { replace: true });
    }
    return undefined;
  }, [isProviderHandoverView, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const sId = submissionId != null ? parseInt(String(submissionId), 10) : NaN;
        const qId = quoteId != null ? parseInt(String(quoteId), 10) : NaN;
        if (!Number.isFinite(sId) || sId <= 0 || !Number.isFinite(qId) || qId <= 0) {
          throw new Error("Invalid handover link");
        }

        if (isProviderHandoverView) {
          const ctxRes = await CommonService.get(API_URLS.LOGISTICS_PROVIDER.RFQ_PICKUP_CONTEXT(sId, qId));
          const ctxBody = ctxRes?.data ?? ctxRes ?? {};
          const ctx = ctxBody?.data ?? ctxBody;
          const rfqData = ctx?.rfq ?? null;
          const q = ctx?.quote ?? null;
          const handoverView = ctx?.handover ?? null;

          if (!cancelled) {
            setRfq(rfqData);
            setQuoteWrapper(
              q
                ? {
                    providerCompanyName: q.providerCompanyName,
                    quote: q.quote && typeof q.quote === "object" ? q.quote : {},
                  }
                : null,
            );
            if (handoverView) {
              setViewOnly(true);
              setExistingPhotos(Array.isArray(handoverView?.photos) ? handoverView.photos : []);
              if (handoverView?.handover && typeof handoverView.handover === "object") {
                setForm((prev) => ({ ...prev, ...handoverView.handover }));
              }
            } else {
              setViewOnly(true);
              toast.warning("Buyer handover is not available for this quote yet.");
            }
            const p = rfqData?.payload && typeof rfqData.payload === "object" ? rfqData.payload : {};
            setForm((prev) => ({
              ...prev,
              pickupDate: prev.pickupDate || isoToYmd(p.pickupDate),
              handoverLocation: prev.handoverLocation || safeStr(p.pickupAddress || "", ""),
              contactPersonName: prev.contactPersonName || safeStr(p.contactPerson || p.contact_name || "", ""),
            }));
          }
        } else {
          const [rfqRes, quoteRes] = await Promise.all([
            CommonService.get(`/company/logistics-providers/rfq/submission/${sId}`),
            CommonService.get(API_URLS.LOGISTICS_PROVIDER.RFQ_MY_SUBMISSION_QUOTE_DETAILS(sId, qId)),
          ]);

          const rfqBody = rfqRes?.data ?? rfqRes ?? {};
          const rfqData = rfqBody?.data ?? rfqBody;

          const quoteBody = quoteRes?.data ?? quoteRes ?? {};
          const quoteData = quoteBody?.data ?? quoteBody;
          const quote = quoteData?.quote || null;

          let handover = null;
          try {
            const hoRes = await CommonService.get(API_URLS.LOGISTICS_PROVIDER.RFQ_MY_SUBMISSION_HANDOVER_GET(sId, qId));
            const hoBody = hoRes?.data ?? hoRes ?? {};
            const hoData = hoBody?.data ?? hoBody;
            handover = hoData?.handover ?? null;
          } catch {
            handover = null;
          }

          if (!cancelled) {
            setRfq(rfqData);
            setQuoteWrapper(quote);
            if (handover) {
              setViewOnly(true);
              setExistingPhotos(Array.isArray(handover?.photos) ? handover.photos : []);
              if (handover?.handover && typeof handover.handover === "object") {
                setForm((prev) => ({ ...prev, ...handover.handover }));
              }
            }
            const p = rfqData?.payload && typeof rfqData.payload === "object" ? rfqData.payload : {};
            setForm((prev) => ({
              ...prev,
              pickupDate: prev.pickupDate || isoToYmd(p.pickupDate),
              handoverLocation: prev.handoverLocation || safeStr(p.pickupAddress || "", ""),
              contactPersonName: prev.contactPersonName || safeStr(p.contactPerson || p.contact_name || "", ""),
            }));
          }
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message || "Could not load handover details.");
        if (!cancelled) {
          setRfq(null);
          setQuoteWrapper(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [submissionId, quoteId, isProviderHandoverView]);

  useEffect(() => {
    let cancelled = false;
    if (!rfq?.payload) return undefined;
    const p = rfq.payload;
    const kind = rfq?.rfqKind != null ? String(rfq.rfqKind).trim().toLowerCase() : "generic";
    const pick = () => {
      if (kind === "business-relocation") {
        return {
          origin: { c: p.currentCountry, s: p.currentState, cy: p.currentCity },
          destination: { c: p.destCountry, s: p.destState, cy: p.destCity },
        };
      }
      if (kind === "exhibition-logistics") {
        return {
          origin: { c: p.orgCountry, s: p.orgState, cy: p.orgCity },
          destination: { c: p.venueCountry, s: p.venueState, cy: p.venueCity },
        };
      }
      return {
        origin: { c: p.originCountry, s: p.originState, cy: p.originCity },
        destination: { c: p.destCountry, s: p.destState, cy: p.destCity },
      };
    };
    (async () => {
      const sel = pick();
      const [o, d] = await Promise.all([
        resolveLocationLabels(sel.origin.c, sel.origin.s, sel.origin.cy),
        resolveLocationLabels(sel.destination.c, sel.destination.s, sel.destination.cy),
      ]);
      if (cancelled) return;
      const join = (x) => [x.city, x.state, x.country].filter(Boolean).join(", ") || "—";
      setResolvedLocations({ origin: join(o), destination: join(d) });
    })();
    return () => {
      cancelled = true;
    };
  }, [rfq?.id]);

  const header = useMemo(() => {
    const quote = quoteWrapper?.quote && typeof quoteWrapper.quote === "object" ? quoteWrapper.quote : {};
    const providerName = quoteWrapper?.providerCompanyName;
    const origin =
      resolvedLocations?.origin ||
      quote.originPort ||
      quote.loadingLocation ||
      quote.origin_port ||
      quote.loading_location;
    const destination =
      resolvedLocations?.destination ||
      quote.destinationPort ||
      quote.unloadingLocation ||
      quote.destination_port ||
      quote.unloading_location;
    const estRaw = quote.estArrival || quote.est_arrival || quote.quoteExpiryDate || quote.quote_expiry_date;

    return {
      orderRef: rfq?.rfqReferenceNumber || rfq?.rfq_reference_number || null,
      providerName: safeStr(providerName),
      origin: safeStr(origin),
      destination: safeStr(destination),
      estDelivery: estRaw ? safeStr(isoToYmd(estRaw) || estRaw) : "—",
    };
  }, [rfq, quoteWrapper, resolvedLocations]);

  const onChange = (key) => (e) => {
    const value = e?.target?.value ?? "";
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onPickFiles = (e) => {
    const raw = Array.from(e?.target?.files || []);
    const images = raw.filter((f) => {
      const t = f?.type != null ? String(f.type) : "";
      if (t.startsWith("image/")) return true;
      const name = (f?.name || "").toLowerCase();
      return /\.(jpe?g|png|gif|webp|heic|bmp|tif|tiff)$/i.test(name);
    });
    if (images.length < raw.length) {
      toast.warning("Only image files can be uploaded for handover photos.");
    }

    const keyOf = (f) => `${f.name}|${f.size}|${f.lastModified}`;
    setUploadFiles((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      const map = new Map();
      for (const f of prevArr) map.set(keyOf(f), f);
      for (const f of images) map.set(keyOf(f), f);
      let merged = Array.from(map.values());
      if (merged.length > MAX_HANDOVER_PHOTOS) {
        toast.warning(`You can upload at most ${MAX_HANDOVER_PHOTOS} images. Extra files were not added.`);
        merged = merged.slice(0, MAX_HANDOVER_PHOTOS);
      }
      return merged;
    });

    const input = e?.target;
    if (input) input.value = "";
  };

  const onComplete = async () => {
    if (isProviderHandoverView) return;
    if (viewOnly) return;
    const validationError = validateHandoverForm(form);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    try {
      const sId = submissionId != null ? parseInt(String(submissionId), 10) : NaN;
      const qId = quoteId != null ? parseInt(String(quoteId), 10) : NaN;
      if (!Number.isFinite(sId) || sId <= 0 || !Number.isFinite(qId) || qId <= 0) {
        throw new Error("Invalid handover link");
      }
      setSaving(true);

      let photoPaths = [];
      const filesToUpload = uploadFiles.slice(0, MAX_HANDOVER_PHOTOS);
      if (filesToUpload.length) {
        const fd = new FormData();
        filesToUpload.forEach((f) => fd.append("files", f));
        // Upload API only allows: company, factory, product, quality, clarification, quotation, auction
        fd.append("category", "company");
        fd.append("companyName", `logistics_handover_submission_${sId}_quote_${qId}`);
        fd.append("fileType", "handover_photo");
        const up = await CommonService.post(API_URLS.UPLOAD.MULTIPLE, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const upBody = up?.data ?? up ?? {};
        const upData = upBody?.data ?? upBody;
        const files = Array.isArray(upData?.files) ? upData.files : [];
        photoPaths = files.map((x) => x?.accessPath).filter(Boolean);
      }

      await CommonService.post(
        API_URLS.LOGISTICS_PROVIDER.RFQ_MY_SUBMISSION_HANDOVER_SAVE(sId, qId),
        { handover: form, photos: photoPaths },
      );

      toast.success("Handover completed.");
      navigate("/logistics-rfqs-sent");
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Could not save handover.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-4 lho-page">
      <div className="container">
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <Link to={dashboardHref}>Dashboard</Link>
            </li>
            {isProviderHandoverView ? (
              <>
                <li className="breadcrumb-item">
                  <Link to="/dashboard/logistics">Logistics</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/dashboard/logistics/rfqs">RFQs received</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Handover
                </li>
              </>
            ) : (
              <>
                <li className="breadcrumb-item">
                  <Link to="/logistics-rfqs-sent">Logistics RFQs sent</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={`/logistics-rfqs-sent/${submissionId}/quotes`}>Compare quotes</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Hand over
                </li>
              </>
            )}
          </ol>
        </nav>

        <div className="lho-topbar mb-3">
          <div className="d-flex flex-column align-items-start gap-2 w-100">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => {
                if (location.state?.from === "pickup-confirmation" && submissionId && quoteId) {
                  navigate(`/dashboard/logistics/rfqs/${submissionId}/pickup/${quoteId}`);
                  return;
                }
                navigate(isProviderHandoverView ? "/dashboard/logistics/rfqs" : "/logistics-rfqs-sent");
              }}
            >
              <i className="fas fa-arrow-left me-1" aria-hidden />
              Back
            </button>
            <div>
              <h2 className="lho-page-title mb-0">Order Fulfillment</h2>
              <div className="lho-subtitle">Order #{safeStr(header.orderRef || `ORD-${safeStr(submissionId)}`)}</div>
            </div>
          </div>
        </div>

        <div className="lho-info-strip mb-3">
          <div className="lho-info-grid">
            <div>
              <div className="lho-info-k">Provider</div>
              <div className="lho-info-v">{header.providerName}</div>
            </div>
            <div>
              <div className="lho-info-k">Origin</div>
              <div className="lho-info-v">{header.origin}</div>
            </div>
            <div>
              <div className="lho-info-k">Destination</div>
              <div className="lho-info-v">{header.destination}</div>
            </div>
            <div>
              <div className="lho-info-k">Est. Delivery</div>
              <div className="lho-info-v">{header.estDelivery}</div>
            </div>
          </div>
        </div>

        <div className="lho-card mb-3">
          <div className="lho-card-head">
            <i className="fas fa-cube" aria-hidden style={{ color: "#1d4ed8", marginTop: 2 }} />
            <div>
              <h5>Goods Handover Details</h5>
              <p>Document the handover of goods to the logistics provider</p>
            </div>
          </div>

          <div className="lho-card-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <div className="lho-section-title">Pickup Information</div>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="lho-field-label">
                        Pickup Date <span className="text-danger">*</span>
                      </div>
                      <div className="lho-input-icon">
                        <input
                          className="form-control lho-input"
                          placeholder="dd-mm-yyyy"
                          type="date"
                          value={form.pickupDate}
                          onChange={onChange("pickupDate")}
                          disabled={viewOnly}
                        />
                        <i className="far fa-calendar" aria-hidden />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="lho-field-label">
                        Pickup Time <span className="text-danger">*</span>
                      </div>
                      <div className="lho-input-icon">
                        <input
                          className="form-control lho-input"
                          placeholder="--:-- --"
                          type="time"
                          value={form.pickupTime}
                          onChange={onChange("pickupTime")}
                          disabled={viewOnly}
                        />
                        <i className="far fa-clock" aria-hidden />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="lho-field-label">
                        Handover Location <span className="text-danger">*</span>
                      </div>
                      <input
                        className="form-control lho-input"
                        placeholder="Warehouse address"
                        value={form.handoverLocation}
                        onChange={onChange("handoverLocation")}
                        disabled={viewOnly}
                      />
                    </div>
                  </div>
                </div>

                <hr className="my-4" />

                <div className="mb-4">
                  <div className="lho-section-title">Contact Person</div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="lho-field-label">
                        <i className="far fa-user" aria-hidden />
                        Contact Person Name <span className="text-danger">*</span>
                      </div>
                      <input
                        className="form-control lho-input"
                        placeholder="John Doe"
                        value={form.contactPersonName}
                        onChange={onChange("contactPersonName")}
                        disabled={viewOnly}
                      />
                    </div>
                    <div className="col-md-6">
                      <div className="lho-field-label">
                        <i className="fas fa-phone" aria-hidden />
                        Contact Phone <span className="text-danger">*</span>
                      </div>
                      <input
                        className="form-control lho-input"
                        placeholder="+91 98765 43210"
                        value={form.contactPhone}
                        onChange={onChange("contactPhone")}
                        disabled={viewOnly}
                      />
                    </div>
                  </div>
                </div>

                <hr className="my-4" />

                <div className="mb-4">
                  <div className="lho-section-title">Vehicle &amp; Driver Information</div>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="lho-field-label">
                        <i className="fas fa-truck" aria-hidden />
                        Vehicle Number <span className="text-danger">*</span>
                      </div>
                      <input
                        className="form-control lho-input"
                        placeholder="MH-01-AB-1234"
                        value={form.vehicleNumber}
                        onChange={onChange("vehicleNumber")}
                        disabled={viewOnly}
                      />
                    </div>
                    <div className="col-md-4">
                      <div className="lho-field-label">
                        Driver Name <span className="text-danger">*</span>
                      </div>
                      <input
                        className="form-control lho-input"
                        placeholder="Driver name"
                        value={form.driverName}
                        onChange={onChange("driverName")}
                        disabled={viewOnly}
                      />
                    </div>
                    <div className="col-md-4">
                      <div className="lho-field-label">
                        Driver Phone <span className="text-danger">*</span>
                      </div>
                      <input
                        className="form-control lho-input"
                        placeholder="+91 98765 43210"
                        value={form.driverPhone}
                        onChange={onChange("driverPhone")}
                        disabled={viewOnly}
                      />
                    </div>
                  </div>
                </div>

                <hr className="my-4" />

                <div className="mb-4">
                  <div className="lho-section-title">Cargo Details</div>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="lho-field-label">
                        <i className="fas fa-boxes" aria-hidden />
                        Number of Packages <span className="text-danger">*</span>
                      </div>
                      <input
                        className="form-control lho-input"
                        type="number"
                        min={1}
                        step={1}
                        inputMode="numeric"
                        placeholder="50"
                        value={form.numberOfPackages}
                        onChange={onChange("numberOfPackages")}
                        disabled={viewOnly}
                      />
                    </div>
                    <div className="col-md-4">
                      <div className="lho-field-label">
                        <i className="fas fa-weight-hanging" aria-hidden />
                        Actual Weight (kg) <span className="text-danger">*</span>
                      </div>
                      <input
                        className="form-control lho-input"
                        type="number"
                        min={0}
                        step="any"
                        inputMode="decimal"
                        placeholder="15000"
                        value={form.actualWeightKg}
                        onChange={onChange("actualWeightKg")}
                        disabled={viewOnly}
                      />
                    </div>
                    <div className="col-md-4">
                      <div className="lho-field-label">
                        <i className="fas fa-hashtag" aria-hidden />
                        Seal Number <span className="text-danger">*</span>
                      </div>
                      <input
                        className="form-control lho-input"
                        placeholder="SEAL-123456"
                        value={form.sealNumber}
                        onChange={onChange("sealNumber")}
                        disabled={viewOnly}
                      />
                    </div>
                  </div>
                </div>

                <hr className="my-4" />

                <div className="mb-4">
                  <div className="lho-section-title">Documentation</div>
                  <div className="lho-upload">
                    <i className="fas fa-camera" aria-hidden />
                    <div className="lho-upload-title">Upload Handover Photos</div>
                    <div className="lho-upload-sub">Click to capture or upload photos of cargo (max {MAX_HANDOVER_PHOTOS} images)</div>
                    {!viewOnly ? (
                      <>
                        <label className="lho-upload-btn">
                          <i className="fas fa-upload" aria-hidden />
                          Upload Photos
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            hidden
                            onChange={onPickFiles}
                          />
                        </label>
                        {uploadFiles.length ? (
                          <div className="mt-2">
                            <div className="small text-muted">
                              {uploadFiles.length === 1
                                ? "1 image selected"
                                : `${uploadFiles.length} images selected`}
                            </div>
                            <div className="small text-muted mt-1">
                              Up to {MAX_HANDOVER_PHOTOS} images total — use Upload again to add more.
                            </div>
                            <ul className="small text-muted mb-0 mt-2 ps-3">
                              {uploadFiles.map((f, idx) => (
                                <li key={`${f.name}-${idx}`} className="text-break">
                                  {f.name}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                      </>
                    ) : existingPhotos.length ? (
                      <div className="small text-muted mt-2">
                        {existingPhotos.length === 1
                          ? "1 photo uploaded"
                          : `${existingPhotos.length} photos uploaded`}
                      </div>
                    ) : (
                      <div className="small text-muted mt-2">No photos uploaded</div>
                    )}
                  </div>
                </div>

                <div className="mb-2">
                  <div className="lho-field-label">Handover Notes</div>
                  <textarea
                    className="form-control lho-input"
                    rows={3}
                    placeholder="Any special notes about the handover..."
                    value={form.notes}
                    onChange={onChange("notes")}
                    disabled={viewOnly}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="lho-footer">
          {!viewOnly ? (
            <button
              type="button"
              className="btn btn-primary d-inline-flex align-items-center gap-2"
              onClick={onComplete}
              disabled={loading || saving}
            >
              {saving ? "Saving..." : "Complete Handover & Continue"}
              <i className="fas fa-arrow-right" aria-hidden />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

